import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { fetchAdminTicketsApi } from '../services/adminTickets'

const getUserId = (state) => {
  const id = state.auth?.userData?.id
  if (id) return id
  try {
    const token = localStorage.getItem('access_token')
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload.sub || payload.id || payload.user_id || null
    }
  } catch (e) {}
  return null
}

// page argument = 0-indexed page number
export const fetchAdminTickets = createAsyncThunk(
  'adminTickets/fetchAdminTickets',
  async (page = 0, thunkAPI) => {
    try {
      const state = thunkAPI.getState()
      const userId = getUserId(state)
      const { statusFilter, titleFilter, limit, cursorStack } = state.adminTickets
      if (!userId) return thunkAPI.rejectWithValue('SESSION_EXPIRED')

      const cursor = cursorStack[page] ?? null
      const result = await fetchAdminTicketsApi({
        userId,
        cursor,
        limit,
        status: statusFilter || undefined,
        title: titleFilter || undefined
      })
      return { ...result, page }
    } catch (err) {
      return thunkAPI.rejectWithValue(err.type || err.message || 'Error desconocido')
    }
  }
)

const initialState = {
  tickets: [],
  hasMore: false,
  cursorStack: [null],  // cursorStack[i] = cursor to fetch page i
  currentPage: 0,
  loading: false,
  error: null,
  statusFilter: '',
  titleFilter: '',
  limit: 10
}

const adminTicketsSlice = createSlice({
  name: 'adminTickets',
  initialState,
  reducers: {
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload
      state.cursorStack = [null]
      state.currentPage = 0
    },
    setTitleFilter: (state, action) => {
      state.titleFilter = action.payload
      state.cursorStack = [null]
      state.currentPage = 0
    },
    setLimit: (state, action) => {
      state.limit = Number(action.payload)
      state.cursorStack = [null]
      state.currentPage = 0
    },
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminTickets.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAdminTickets.fulfilled, (state, action) => {
        state.loading = false
        state.tickets = action.payload.tickets || []
        state.hasMore = action.payload.hasMore || false
        state.currentPage = action.payload.page

        // Store next cursor only at the frontier (don't overwrite on back-navigation)
        if (action.payload.hasMore && action.payload.nextCursor) {
          const nextPage = action.payload.page + 1
          if (state.cursorStack.length <= nextPage) {
            state.cursorStack.push(action.payload.nextCursor)
          }
        }
      })
      .addCase(fetchAdminTickets.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Error desconocido'
      })
  }
})

export const { setStatusFilter, setTitleFilter, setLimit, clearError } = adminTicketsSlice.actions

export const selectAdminTickets = (state) => state.adminTickets.tickets
export const selectAdminTicketsCurrentPage = (state) => state.adminTickets.currentPage
export const selectAdminTicketsCursorStack = (state) => state.adminTickets.cursorStack
export const selectAdminTicketsHasMore = (state) => state.adminTickets.hasMore
export const selectAdminTicketsLoading = (state) => state.adminTickets.loading
export const selectAdminTicketsError = (state) => state.adminTickets.error
export const selectAdminTicketsStatusFilter = (state) => state.adminTickets.statusFilter
export const selectAdminTicketsTitleFilter = (state) => state.adminTickets.titleFilter
export const selectAdminTicketsLimit = (state) => state.adminTickets.limit

export default adminTicketsSlice.reducer
